import { Component } from '@theme/component';

/**
 * Drives the Admira three-pane mega menu (SPEC §3.4).
 *
 * Three jobs, and nothing else:
 *
 * 1. **Selection.** Pointer or focus on a pane-1 item shows that item's pane-2
 *    list and asks for its products.
 * 2. **Deferred pane 3.** Product cards are never in the initial HTML. The first
 *    time a category is selected its collection is fetched through the Section
 *    Rendering API, and the result is cached for the life of the page.
 * 3. **Roving tabindex.** The whole panel is one tab stop. Arrow keys move
 *    within a pane and between panes; `Esc` closes.
 *
 * It is deliberately not focus-trapped. A menu is not a dialog, and tabbing past
 * it is how a reader who does not want it gets on with the page.
 */
class AdmiraMegaMenu extends Component {
  requiredRefs = ['primaryLinks'];

  /**
   * Fetched pane-3 markup, keyed by collection URL. Only ever grows, and only
   * ever by one entry per category the reader actually opened.
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /** @type {AbortController | null} */
  #inFlight = null;

  /** @type {number} */
  #selected = -1;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);

    // The first item is selected on open rather than on load: selecting here
    // would fire a fetch for a menu nobody has looked at yet.
    this.#observeOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#inFlight?.abort();
    this.#observer?.disconnect();
  }

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * The submenu is collapsed with `inert`, so "opened" is the first moment the
   * panel has a box. An IntersectionObserver is cheaper and more reliable here
   * than listening for the several ways the vendor header opens a submenu
   * (pointer, click, keyboard, and the design-mode re-render).
   */
  #observeOpen() {
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#observer?.disconnect();
        this.#select(0, { focus: false });
      }
    });

    this.#observer.observe(this);
  }

  /**
   * Bound from the markup by `on:pointerenter` and `on:focus`.
   * @param {Event} event
   */
  select(event) {
    // Declarative handlers are delegated from `document`, so `currentTarget`
    // is the document; `target` is proxied to the element with the attribute.
    const target = event.target;
    const link = target instanceof Element ? target.closest('[data-pane-index]') : null;
    if (!(link instanceof HTMLElement)) return;

    const index = Number(link.dataset.paneIndex);
    if (Number.isNaN(index)) return;

    this.#select(index, { focus: false });
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #select(index, options) {
    this.#fit();
    const { primaryLinks, secondaryLists } = this.refs;
    const links = toArray(primaryLinks);
    const target = links[index];
    if (!target) return;

    this.#selected = index;

    for (const [i, link] of links.entries()) {
      const current = i === index;
      link.setAttribute('aria-current', current ? 'true' : 'false');
      // Roving tabindex: exactly one of these is ever tabbable.
      link.tabIndex = current ? 0 : -1;
    }

    let hasSecondary = false;
    for (const list of toArray(secondaryLists)) {
      const current = Number(list.dataset.paneIndex) === index;
      list.hidden = !current;
      list.toggleAttribute('inert', !current);
      if (current && list.querySelector('li')) hasSecondary = true;

      for (const link of list.querySelectorAll('a')) {
        link.tabIndex = -1;
      }
    }

    // No subcategories: pane 2 collapses and pane 3 takes its room (OI-125).
    this.toggleAttribute('data-no-secondary', !hasSecondary);

    if (options?.focus) target.focus();

    this.#loadProducts(target.dataset.productsUrl);
  }

  /**
   * The panel may never scroll (OI-125), so its height is capped at the room
   * between its own top and the bottom of the viewport. Measured, not derived
   * from `--header-group-height`: that variable does not say where this panel
   * starts, and a cap read from it clipped "Vezi toate produsele" on a 650px
   * window.
   */
  #fit() {
    const top = this.getBoundingClientRect().top;
    if (top <= 0) return;
    this.style.setProperty('--mega-available', `${Math.max(320, Math.floor(window.innerHeight - top))}px`);
  }

  /**
   * Fills pane 3 for a collection, from cache when it has been opened before.
   * @param {string | undefined} url
   */
  async #loadProducts(url) {
    const pane = this.refs.featuredPane;
    if (!(pane instanceof HTMLElement)) return;

    if (!url) {
      pane.replaceChildren();
      return;
    }

    const cached = this.#cache.get(url);
    if (cached !== undefined) {
      pane.innerHTML = cached;
      return;
    }

    // A reader moving down pane 1 fires a request per item. Only the last one
    // matters, so the previous is abandoned rather than raced.
    this.#inFlight?.abort();
    const controller = new AbortController();
    this.#inFlight = controller;

    pane.setAttribute('aria-busy', 'true');

    try {
      const sectionId = pane.dataset.sectionId ?? 'admira-mega-menu-products';
      const endpoint = new URL(url, window.location.origin);
      endpoint.searchParams.set('section_id', sectionId);

      const response = await fetch(endpoint, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));

      const html = await response.text();
      this.#cache.set(url, html);

      // Guard against a slow response for a category the reader has since moved
      // off: only paint if this is still the request we are waiting for.
      if (this.#inFlight === controller) pane.innerHTML = html;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // A failed fetch leaves pane 3 empty, which is the section's documented
      // empty state — not a broken box and not an error message in a menu.
      pane.replaceChildren();
    } finally {
      if (this.#inFlight === controller) {
        this.#inFlight = null;
        pane.setAttribute('aria-busy', 'false');
      }
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const links = toArray(this.refs.primaryLinks);
    if (links.length === 0) return;

    const withinPrimary = links.includes(/** @type {HTMLElement} */ (event.target));

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!withinPrimary) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (this.#selected + step + links.length) % links.length;
        this.#select(next, { focus: true });
        break;
      }

      case 'ArrowRight': {
        if (!withinPrimary) return;
        const first = this.#visibleSecondaryLinks()[0];
        if (!first) return;
        event.preventDefault();
        first.tabIndex = 0;
        first.focus();
        break;
      }

      case 'ArrowLeft': {
        if (withinPrimary) return;
        event.preventDefault();
        this.#select(this.#selected, { focus: true });
        break;
      }

      default:
        break;
    }
  };

  /** @returns {HTMLAnchorElement[]} */
  #visibleSecondaryLinks() {
    const lists = toArray(this.refs.secondaryLists);
    const list = lists.find((candidate) => !candidate.hidden);
    return list ? Array.from(list.querySelectorAll('a')) : [];
  }
}

/**
 * `ref="x[]"` yields an array, a bare `ref="x"` a single node, and an absent one
 * `undefined`. Normalising once here keeps every call site from re-checking.
 *
 * @param {unknown} value
 * @returns {HTMLElement[]}
 */
function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item instanceof HTMLElement);
  return value instanceof HTMLElement ? [value] : [];
}

if (!customElements.get('admira-mega-menu')) {
  customElements.define('admira-mega-menu', AdmiraMegaMenu);
}
